import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dataService } from '../../services/dataService';
import { Card } from '../Card';
import { School, District, CsvImport, AuditLog } from '../../data/types';
import PageContainer from '../layouts/PageContainer';

const SuperAdminDashboardScreen: React.FC = () => {
  const [district, setDistrict] = useState<District | null>(null);
  const [schools, setSchools] = useState<School[]>([]);
  
  const [recentAuditLogs, setRecentAuditLogs] = useState<AuditLog[]>([]);
const [districts, setDistricts] = useState<District[]>([]);
const [learningPaths, setLearningPaths] = useState<any[]>([]);
const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [districtsData, schoolsData, logsData, learningPathsData] = await Promise.all([
          dataService.getDistricts(),
          dataService.getSchools(),
          dataService.getAuditLogs(),
          dataService.getLearningPaths()
        ]);
        setDistricts(districtsData);
        setDistrict(districtsData[0] || null); // For primary overview
        setSchools(schoolsData);
        setRecentAuditLogs(logsData.slice(0, 5));
        setLearningPaths(learningPathsData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Format timestamp to readable date/time
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format import status with appropriate color
  const getStatusBadge = (status: string) => {
    let bgColor = '';
    let textColor = '';
    
    switch(status) {
      case 'success':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'partial':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'failed':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      case 'processing':
        bgColor = 'bg-blue-100';
        textColor = 'text-blue-800';
        break;
      default:
        bgColor = 'bg-gray-100';
        textColor = 'text-gray-800';
    }
    
    return (
      <span className={`${bgColor} ${textColor} px-2 py-1 rounded-full text-xs font-medium`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <PageContainer title="Super Admin Dashboard" userRole="SuperAdmin">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="Super Admin Dashboard" userRole="SuperAdmin">
      
      {/* District Overview */}
      <div className="mb-8">
        <Card className="bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-800">District Overview</h2>
            <span className="text-sm text-gray-500">Last Synced: {district ? formatTimestamp(district.lastSynced) : 'N/A'}</span>
          </div>
          
          {district && (
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-3">{district.name}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-blue-100 mr-4">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Schools</p>
                      <p className="text-2xl font-bold">{district.totalSchools}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-green-100 mr-4">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Students</p>
                      <p className="text-2xl font-bold">{district.totalStudents.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-purple-100 mr-4">
                      <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Teachers</p>
                      <p className="text-2xl font-bold">{district.totalTeachers.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-4">
                <div>
                  <span className="text-sm font-medium text-gray-600">District Default Progression Rule:</span>
                  <span className="ml-2 text-sm text-gray-500">80% accuracy threshold</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">Skip and Move to Game:</span>
                  <span className="ml-2 text-sm text-green-500">Enabled</span>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
      
      {/* Main Dashboard Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity and School Overview */}
        <div className="lg:col-span-2 space-y-6">
          
          
          {/* School Overview */}
          <Card className="bg-white p-4 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">School Overview</h2>
              <select className="border border-gray-300 rounded px-2 py-1 text-sm">
                <option>All Schools</option>
                <option>Elementary</option>
                <option>Middle</option>
                <option>High School</option>
              </select>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School</th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Students</th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teachers</th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {schools.slice(0, 5).map((school) => (
                    <tr key={school.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{school.name}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {Math.floor(Math.random() * 1000) + 200}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {Math.floor(Math.random() * 50) + 10}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          Active
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 text-center">
              <button className="text-primary hover:text-primary-dark text-sm font-medium">
                Manage Schools
              </button>
            </div>
          </Card>

          {/* Recent Activity */}
          <Card className="bg-white p-4 shadow-sm">
            <h2 className="text-lg font-bold mb-4">Recent Activity</h2>
            <div className="space-y-4">
              {recentAuditLogs.map((log) => (
                <div key={log.id} className="flex items-start">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-600 text-sm font-bold">{log.userName.substring(0, 2).toUpperCase()}</span>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      <span className="font-semibold">{log.userName}</span> {log.details}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatTimestamp(log.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-center">
              <button className="text-primary hover:text-primary-dark text-sm font-medium">
                View All Activity
              </button>
            </div>
          </Card>
          
        </div>
        
        {/* Right Sidebar */}
        <div className="space-y-6">
          
          
          {/* Usage Analytics */}
          <Card className="bg-white p-4 shadow-sm">
            <h2 className="text-lg font-bold mb-4">Usage Analytics</h2>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-700">Daily Active Users</span>
                  <span className="text-sm font-medium text-green-600">+12%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: '78%' }}></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0</span>
                  <span>35,000 users</span>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-700">Content Engagement</span>
                  <span className="text-sm font-medium text-green-600">+8%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full" style={{ width: '65%' }}></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0</span>
                  <span>100%</span>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-700">Standards Completion</span>
                  <span className="text-sm font-medium text-yellow-600">+3%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '42%' }}></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0</span>
                  <span>100%</span>
                </div>
              </div>
            </div>
            <div className="mt-4 text-center">
              <button className="text-primary hover:text-primary-dark text-sm font-medium">
                View Detailed Analytics
              </button>
            </div>
          </Card>
          
          {/* System Status */}
          <Card className="bg-white p-4 shadow-sm">
            <h2 className="text-lg font-bold mb-4">System Status</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Database</span>
                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium">Healthy</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">API Server</span>
                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium">Healthy</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Content Delivery</span>
                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium">Healthy</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Last SSO Sync</span>
                <span className="text-xs text-gray-600">2 hours ago</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </PageContainer>
  );
};

export default SuperAdminDashboardScreen;
